/**
 * useOnboarding - First-use detection for the welcome tutorial (OnboardingTour).
 * v1 approach: localStorage only (no Firestore / Cloud Functions involved).
 */

import { useCallback, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const ONBOARDING_STORAGE_KEY = 'onboarding-completed-v1';

function readCompletedFlag(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_STORAGE_KEY) !== null;
  } catch {
    // If localStorage is unavailable, never show the tour repeatedly
    return true;
  }
}

export function useOnboarding() {
  const { user } = useAuth();
  const [completed, setCompleted] = useState<boolean>(readCompletedFlag);

  // Only for authenticated users (including anonymous) with client/guest role
  const shouldShowOnboarding =
    !!user && (user.role === 'client' || user.role === 'guest') && !completed;

  const completeOnboarding = useCallback(() => {
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, new Date().toISOString());
    } catch {
      // Ignore storage errors; state still hides the tour for this session
    }
    setCompleted(true);
  }, []);

  return { shouldShowOnboarding, completeOnboarding };
}
